import { captureGenericError } from 'src/lib/sentry';
import { GCInstant } from '../../lib/gcinstant';
import { Notifications } from '@play-co/native-game-js/modules/Notifications';
import StateObserver from 'src/StateObserver';
import { setUserProperties, track } from 'src/lib/analytics/utils';
import { assertNever } from 'src/replicant/utils';
import { getMatch2Level } from 'src/replicant/match2/match2.getters';
import * as Promo from 'src/replicant/promo';
import {
  mapFeatureToDialogOrigin,
  openPopupPromise,
} from 'src/lib/popups/popupOpenClose';
import { duration } from 'src/replicant/utils/duration';

/**
 * Push Notification Status Types
 */
type PushNotificationStatus =
  | 'unknown' // We nor sure what the status is
  | 'on' // User has granted permission to receive push notifications
  | 'blocked' // User has denied permission to receive push notifications
  | 'never_set'; // User has never been asked for permission to receive push notifications

/**
 * Push Notification User Properties for Analytics
 */
type PushNotificationUserProperties = {
  isSubscribedPN: boolean | undefined;
  isPushNotificationRegistered: boolean | undefined;
  pushNotificationStatus: string | undefined;
  isPushNotificationApiAvailable: boolean | undefined;
  isAppleDeviceTokenSaved: boolean | undefined;
  isAndroidDeviceTokenSaved: boolean | undefined;
};

/**
 * We precalculate user properties for push notifications in updatePushNotificationStatus
 */
const analyticsUserProperties: PushNotificationUserProperties = {
  isSubscribedPN: undefined,
  isPushNotificationRegistered: undefined,
  pushNotificationStatus: undefined,
  isPushNotificationApiAvailable: undefined,
  isAppleDeviceTokenSaved: undefined,
  isAndroidDeviceTokenSaved: undefined,
};

/**
 * Retrieves the current push notification authorization status.
 *
 * This function queries the `Notifications` instance for the user's authorization status
 * and maps it to a corresponding `PushNotificationStatus` value.
 *
 * Possible return values:
 * - `'unknown'`: The status is could not be determined.
 * - `'never_set'`: The user has not set a notification preference.
 * - `'blocked'`: Notifications are denied by the user.
 * - `'on'`: Notifications are authorized
 *
 * @returns {Promise<PushNotificationStatus>} A promise that resolves to the current push notification status.
 */
async function getPushNotificationStatus(): Promise<PushNotificationStatus> {
  const notifications = Notifications.getInstance();

  if (!notifications?.authorizationStatus) {
    analyticsUserProperties.pushNotificationStatus = 'unknown';
    return 'unknown';
  }

  const authorizationStatus = await notifications.authorizationStatus();

  // Update analytics user properties every time we check the status
  analyticsUserProperties.pushNotificationStatus = authorizationStatus;
  analyticsUserProperties.isPushNotificationRegistered =
    await notifications.isRegistered(); // Should be relatively quick

  switch (authorizationStatus) {
    case 'provisional':
    case 'ephemeral':
      return 'on'; // Technically it's maybe be on but with some restriction so we treat it as on
    case 'not determined':
      return 'never_set';
    case 'denied':
      return 'blocked';
    case 'authorized':
      return 'on';
    default:
      return 'unknown';
  }
}

/**
 * Determines whether the Push Notifications API is available for use.
 *
 * This function first checks for the presence of a notifications module (available on iOS app v2.12 or higher).
 * If the module exists, it assumes push notifications can be registered.
 * If not, it checks if the code is running on a native app platform.
 * If neither condition is met, it falls back to the legacy notifications API to determine availability.
 *
 * @returns {boolean} `true` if push notifications can be registered; otherwise, `false`.
 */
function isPushNotificationsApiAvailable(): boolean {
  // Cannot register if not on native app.
  const os = GCInstant.legacyNative?.platform.os;

  // Check if we are on native platform
  if (os !== 'android' && os !== 'ios') return false;

  // If there is a notifications module (iOS app v2.12 or higher) use that instead to check if user can register
  const notificationsModule = Notifications.getInstance();
  if (notificationsModule) {
    return true;
  }

  // Otherwise, fallback to the legacy notifications API
  return GCInstant.legacyNative?.notifications.isAvailable() ?? false;
}

/**
 * Determines whether the app should initiate (or re-initiate) registration for push notifications.
 *
 * The decision considers:
 * - Availability of the push notifications API.
 * - System notification status (`on` or `unknown` treated as enabled).
 * - Presence of a stored device token.
 * - Native registration state reported by the notifications module (iOS app v2.12+).
 * - A daily token refresh policy gated by user progress (level >= 15) and whether the push reminder promo has been shown at least once.
 *
 * Returns `true` if any of the following hold:
 * - Notifications are enabled but no device token is present.
 * - Notifications are enabled and the native module reports the device is not registered.
 * - Notifications are enabled, at least one day has passed since the last token update, and the user has reached level 15+ and has seen the push reminder promo.
 *
 * Otherwise returns `false`.
 *
 * Note: This function only decides whether to register; it does not perform registration itself.
 *
 * @returns Promise that resolves to `true` when registration should be attempted; otherwise `false`.
 *
 * Remarks: Reads global/user state and queries native notification APIs; the promise may reject if underlying calls fail.
 */
async function shouldRegisterForPushNotifications(): Promise<boolean> {
  if (!isPushNotificationsApiAvailable()) return false;

  const { user } = StateObserver.getState();
  const now = StateObserver.now();
  const status = await getPushNotificationStatus();
  const isEnabled = status === 'on' || status === 'unknown';
  const notificationsModule = Notifications.getInstance();
  const tokenUpdateAt = user.native.pushNotificationTokenUpdatedAt;
  const promoWasShown =
    Promo.getters.getPromoShownCounter(user, 'pushNotificationReminderV2') >= 1;
  const hasDeviceToken = !!getDeviceToken();

  // If enabled but no token, we need to register
  if (isEnabled && !hasDeviceToken) {
    return true;
  }

  // Register if system reports we are not registered yet
  if (isEnabled && notificationsModule) {
    const isRegistered = await notificationsModule.isRegistered();
    if (!isRegistered) {
      return true;
    }
  }

  // Update token once per day if user already saw promo and >15 level
  const day = duration({ days: 1 });
  const lastUpdateDuration = now - (tokenUpdateAt || 0);
  if (isEnabled && lastUpdateDuration > day) {
    return getMatch2Level(user) >= 15 && promoWasShown;
  }

  return false;
}

/**
 * Retrieves the platform-specific device token used for push notifications.
 *
 * - On Android, returns the token provided by `StateObserver.replicant.getAndroidDeviceToken()`.
 * - On iOS, returns the token provided by `StateObserver.replicant.getAppleDeviceToken()`.
 * - On other platforms, or if no token is available, returns `undefined`.
 *
 * @returns The device token string for the current platform, or `undefined` if unsupported or unavailable.
 */
function getDeviceToken() {
  const os = GCInstant.legacyNative?.platform.os;

  if (os === 'android') {
    return StateObserver.replicant.getAndroidDeviceToken();
  }

  if (os === 'ios') {
    return StateObserver.replicant.getAppleDeviceToken();
  }

  return undefined;
}

/**
 * Registers the device for push notifications using the appropriate module.
 *
 * Attempts to use the new notifications module (available on iOS app v2.12 or higher).
 * Falls back to legacy registration if the module is not available.
 * Handles registration success and errors, invoking `onPushNotificationRegistration`
 * with the device token or `undefined` on failure.
 *
 * @returns A promise that resolves to `true` if registration was successful, otherwise `false`.
 */
async function registerForPushNotifications(): Promise<boolean> {
  // Try to get the notifications module (iOS app v2.12 or higher)
  const notificationsModule = Notifications.getInstance();

  // Legacy fallback if module not available
  if (!notificationsModule) {
    return await registerForPushNotificationsLegacy();
  }

  // New notifications module registration check, only for iOS v2.12+
  try {
    const result = await notificationsModule.register();

    if (result.type === 'success') {
      return onPushNotificationRegistration(result.deviceToken);
    }
  } catch (error) {
    console.error('Error during push notification registration:', error);
    captureGenericError('Failed to register for push notification', error);
    return onPushNotificationRegistration(undefined);
  }

  // If we reach here, it means we couldn't register for push notifications
  return onPushNotificationRegistration(undefined);
}

/**
 * Attempts to register the device for push notifications using a legacy native module.
 *
 * If the legacy native module is available (`GCInstant.legacyNative`), it calls the registration method
 * and, upon success, passes the device token to `onPushNotificationRegistration`.
 * If registration fails or the module is unavailable, it calls `onPushNotificationRegistration` with `undefined`.
 *
 * @returns A promise that resolves to `true` if registration was successful, otherwise `false`.
 */
async function registerForPushNotificationsLegacy(): Promise<boolean> {
  // Fallback to legacy native registration if module not available
  if (GCInstant.legacyNative) {
    const result = await GCInstant.legacyNative.notifications.register({});
    if (result.success === true && result.deviceToken) {
      return onPushNotificationRegistration(result.deviceToken);
    }
  }

  // If we reach here, it means we couldn't register for push notifications
  return onPushNotificationRegistration(undefined);
}

/**
 * Handles push notification registration by updating user settings and device token.
 *
 * This function performs the following actions:
 * - Determines the operating system from the native platform.
 * - Updates the user's push notification settings based on the presence of a device token.
 * - Sets the native device token for push notifications.
 * - Updates user analytics properties to reflect push notification permission status.
 * - Handles and logs any errors that occur during the update process.
 *
 * @param deviceToken - The device token for push notifications, or `undefined` if notifications are disabled.
 */
async function onPushNotificationRegistration(
  deviceToken: string | undefined,
): Promise<boolean> {
  // User has disabled notifications, clear the token.
  const os = GCInstant.legacyNative?.platform.os;
  if (!os) return false;

  // We update flag for replicant actions to just know that we can have push notifications on server side
  const updateSettingFlag = StateObserver.invoke.updateSettings({
    pushNotifications: !!deviceToken,
  });

  // Update the push notification device token
  const updateDeviceToken = StateObserver.invoke.setNativeDeviceToken({
    [os]: deviceToken ?? '',
  });

  try {
    // Wait for both updates to complete
    await Promise.all([updateSettingFlag, updateDeviceToken]);

    // Update user analytics property
    setUserProperties({
      inGamePushNotifPermitted: !!deviceToken,
    });

    // Update user settings after registration attempt
    await updatePushNotificationStatus();

    // Track the registration result
    track('PushNotificationRegistration', {
      success: !!deviceToken,
      os,
      ...analyticsUserProperties,
    });

    return !!deviceToken;
  } catch (error) {
    console.error('Error updating push notification settings:', error);
    captureGenericError('Failed to update push notification settings', error);
    return false;
  }
}

/**
 * Determines whether push notifications are enabled for the current device and platform.
 *
 * This function checks the operating system, user settings, and device tokens to
 * decide if push notifications are enabled. For iOS, it also checks the push notification
 * status, treating 'unknown' status as enabled due to legacy behavior.
 *
 * @returns A promise that resolves to `true` if push notifications are enabled, otherwise `false`.
 */
async function arePushNotificationEnabled(): Promise<boolean> {
  const os = GCInstant.legacyNative?.platform.os;
  if (!os) return false;

  const { replicant } = StateObserver;

  switch (os) {
    case 'android':
      return !!replicant.getAndroidDeviceToken();
    case 'ios': {
      const status = await getPushNotificationStatus();
      // From legacy ios we will get only unknown status, so we assume that it's enabled
      const isEnabled =
        status === 'on' || status === 'unknown' || status === 'never_set';
      return !!replicant.getAppleDeviceToken() && isEnabled;
    }
    default:
      assertNever(os);
  }
}

/**
 * Determines whether the application should request permission for push notifications.
 *
 * This function checks the following conditions:
 * - The user has never registered for push notifications or the status is unknown.
 * - The push notifications API is available.
 * - The user has never seen the push notification promo.
 *
 * @returns A promise that resolves to `true` if the permission request should be shown, otherwise `false`.
 */
async function shouldRequestForPushNotificationPermission(): Promise<boolean> {
  const { user } = StateObserver.getState();
  const status = await getPushNotificationStatus();

  // Get how many times user saw this promo
  const wasShownTimes = Promo.getters.getPromoShownCounter(
    user,
    'pushNotificationReminderV2',
  );
  const wasNeverShown = wasShownTimes === 0;

  // If user never registered for push notifications or we can't detect the status
  const isStatusAllowRegistration =
    status === 'never_set' || status === 'unknown';

  // Is push notifications API available
  const isApiAvailable = isPushNotificationsApiAvailable();

  // Full condition to show first time prompt for push notifications
  return isStatusAllowRegistration && isApiAvailable && wasNeverShown;
}

/**
 * Determines whether the application should request push notification (PN) permission reminder
 * from the user based on various conditions.
 *
 * @returns {Promise<boolean>} `true` if the conditions for requesting PN permission are met, otherwise `false`.
 *
 * The conditions checked are:
 * - The user's match level is at least 15.
 * - A specific promotional campaign (`pushNotificationReminderV2`) is ready for the user.
 * - The user's push notification settings are not explicitly disabled.
 * - The platform supports registering for push notifications.
 *
 * @see Spec: https://docs.google.com/document/d/12v0m7zCU5p5A4dun_ZMAU86fOn2KmFxEd_vKYl72toM/edit?usp=sharing
 */
async function shouldRemindForPushNotificationPermission(): Promise<boolean> {
  const { user } = StateObserver.getState();
  const now = StateObserver.now();
  const status = await getPushNotificationStatus();
  const isPNsDisabled = status !== 'on';
  const isApiAvailable = isPushNotificationsApiAvailable();

  return (
    isPNsDisabled &&
    isApiAvailable &&
    getMatch2Level(user) >= 15 &&
    Promo.getters.isPromoReady(user, 'pushNotificationReminderV2', now)
  );
}

/**
 * Update push notification status in foreground.
 * Should be called in session preparation phase.
 */
async function updatePushNotificationStatus(): Promise<void> {
  // Try get real push notification status
  const isPnsEnabled = await arePushNotificationEnabled();
  const { replicant } = StateObserver;

  // Update analytics user properties
  analyticsUserProperties.isSubscribedPN = isPnsEnabled;
  analyticsUserProperties.isPushNotificationApiAvailable =
    isPushNotificationsApiAvailable();
  analyticsUserProperties.isAppleDeviceTokenSaved =
    !!replicant.getAppleDeviceToken();
  analyticsUserProperties.isAndroidDeviceTokenSaved =
    !!replicant.getAndroidDeviceToken();

  // We update flag for replicant actions to just know that we can have push notifications on server side
  await StateObserver.invoke.updateSettings({
    pushNotifications: isPnsEnabled,
  });
}

/**
 * Requests push notification permission from the user.
 *
 * This function handles the logic for showing the system permission dialog
 * for push notifications and tracking the user's response. It also includes
 * a reminder mechanism for subsequent requests if the user initially denies
 * the permission. The function ensures that the appropriate system settings
 * are shown to the user based on their platform (iOS or Android).
 *
 * @returns {Promise<boolean>} A promise that resolves to `true` if the user
 * successfully enables push notifications, or `false` otherwise.
 *
 * ## Behavior:
 * - On the first request, it shows a permission dialog and tracks the user's response.
 * - On subsequent requests, it shows a reminder popup and redirects the user
 *   to the system settings if necessary.
 * - Tracks user interactions with the permission and reminder dialogs.
 *
 * ## Platform-Specific Notes:
 * - On iOS, redirects the user to the app settings if they accept the reminder popup.
 * - Android-specific behavior is currently a TODO and requires further implementation.
 *
 * ## Tracking:
 * - Tracks user responses to the permission and reminder dialogs using the `track` function.
 * - Resets the promo counter for the push notification reminder.
 *
 * @see Spec: https://docs.google.com/document/d/12v0m7zCU5p5A4dun_ZMAU86fOn2KmFxEd_vKYl72toM/edit?usp=sharing
 */
async function requestPushNotificationPermission(
  origin: 'launch' | 'postLevel' | 'settings',
): Promise<boolean> {
  const { user } = StateObserver.getState();

  // Check if we can register for push notifications in current platform
  if (!isPushNotificationsApiAvailable()) {
    return false;
  }

  // We have only one system request for push notifications.
  const wasShownTimes = Promo.getters.getPromoShownCounter(
    user,
    'pushNotificationReminderV2',
  );

  // Reset the reminder promo, it will be shown if user later disables notifications.
  await StateObserver.invoke.promoWasShown('pushNotificationReminderV2');

  // Is user never set push notification on thi device we can detect it over status
  const status = await getPushNotificationStatus();

  // Check if user has never been asked for push notification permission
  const isFirstTime = status === 'never_set';

  // First time user asked about notification permission we expect to have a system request for push notifications
  if (isFirstTime) {
    // Show permission dialog request
    const accepted = await openPopupPromise('popupPNPermission', {
      origin: mapFeatureToDialogOrigin(origin),
    });

    // Track user response to the game permission dialog
    track('RespondGamePushNotificationPermissionPopup', {
      puzzleLevel: getMatch2Level(user),
      response: accepted ? 'ok' : 'no',
      requestCount: wasShownTimes,
      origin,
    });

    // Prompt user to enable notifications
    return await registerForPushNotifications();
  }

  // -------------------------------------------------
  // For the next requests we show the reminder popup because the system request was already shown
  // But we try to redirect user to the system settings where user can enable notifications

  // Check user already enabled notifications settings.
  // It won't show the system popup, but will return true if user already enabled notifications
  // If it returns false, we show the reminder popup and ask user to enable notifications
  const success = await registerForPushNotifications();

  // We don't need to show the reminder popup if user has enabled notifications already
  if (success) {
    return true;
  }

  // Start reminder logic
  const accepted = await openPopupPromise('popupPNPermissionReminder', {
    origin: mapFeatureToDialogOrigin(origin),
  });

  // Track user response to the game permission dialog
  track('RespondGamePushNotificationPermissionPopup', {
    puzzleLevel: getMatch2Level(user),
    response: accepted ? 'ok' : 'no',
    requestCount: wasShownTimes,
    origin,
  });

  // If user accepted, start the process to register for push notifications
  if (accepted) {
    const os = GCInstant.legacyNative?.platform.os;
    // Show app setting where use can turn on notifications un case system request blocked
    if (os === 'ios') {
      window.location.href = 'app-settings:';
    }

    // For android
    // if (os === 'android') {
    // TODO: get proper fix for android:  https://github.com/play-co/native-builder/issues/190
    // }

    return true;
  }

  return false;
}

/**
 * Sync version of arePushNotificationEnabled.
 * Should be used when you do not expect it be 100% accurate.
 * It will be sync every login but user can turn of notification in system settings after login.
 *
 * @returns true if user can use push notifications, false otherwise
 */
function arePushNotificationEnabledSync(): boolean {
  return StateObserver.getState().user.settings.pushNotifications ?? false;
}

/**
 * Return copy of push notification user properties for analytics.
 *
 * @returns Push Notification User Properties for Analytics
 */
function getPushNotificationUserProperties(): PushNotificationUserProperties {
  return { ...analyticsUserProperties };
}

// Export public API
const PNsManager = {
  isApiAvailable: isPushNotificationsApiAvailable,
  arePushNotificationEnabledSync,
  shouldRequestForPermission: shouldRequestForPushNotificationPermission,
  shouldRemindPermissionRequest: shouldRemindForPushNotificationPermission,
  requestPermission: requestPushNotificationPermission,
  updateStatus: updatePushNotificationStatus,
  getPushNotificationUserProperties,
  shouldRegister: shouldRegisterForPushNotifications,
  register: registerForPushNotifications,
};

export default PNsManager;
